import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EuroIcon from "@mui/icons-material/Euro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import Loading from "../../components/Loading/Loading";
import { useUserState } from "../../state/user";
import { fetchFichasClinicas } from "../../services/user";
import { useTranslation } from "react-i18next";
import PatientDetailPanel from "./PatientDetailPanel";
import PatientVideoDialog from "./PatientVideoDialog";
import { useAppointments } from "../../hooks/useAppointments";
import { typography } from "../../utils/typography";
import { semanticColors } from "../../utils/colors";

function calculateAge(birthDate: string): string {
  if (!birthDate || birthDate === "Invalid date") return "-";
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return "-";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate()))
    age--;
  return String(age);
}

const pageSx = {
  bgcolor: semanticColors.surface.canvas,
  minHeight: "100vh"
};

const stickyBarSx = {
  bgcolor: "#FCFCFF",
  boxShadow: "0px 4px 6px rgba(30, 39, 79, 0.1)",
  position: "sticky",
  top: 80,
  zIndex: 10
};

const tabsSx = {
  "& .MuiTabs-indicator": {
    backgroundColor: "#0DBDA7",
    height: "2px"
  },
  minHeight: 47,
  px: { xs: "16px", sm: "24px" }
};

const utentesTabSx = {
  minHeight: 47,
  ...typography.body.m,
  fontWeight: 600,
  color: "#0DBDA7",
  textTransform: "none",
  "&.Mui-selected": { color: "#0DBDA7" }
};

const contentSx = {
  maxWidth: "920px",
  mx: "auto",
  px: { xs: "16px", sm: "32px" },
  pt: "32px",
  pb: "48px"
};

const searchIconSx = { color: semanticColors.text.secondary, fontSize: 18 };

const searchFieldSx = {
  mb: "16px",
  "& .MuiOutlinedInput-root": {
    height: "52px",
    borderRadius: "8px",
    bgcolor: semanticColors.surface.canvas,
    fontFamily: "'Inter', sans-serif",
    "& fieldset": { borderColor: semanticColors.border.middle },
    "&:hover fieldset": { borderColor: semanticColors.text.primary },
    "&.Mui-focused fieldset": {
      borderColor: semanticColors.border.focus
    }
  },
  "& .MuiInputBase-input": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "16px",
    color: semanticColors.text.primary
  }
};

const noResultsSx = {
  ...typography.body.l,
  color: semanticColors.text.secondary,
  mt: 4,
  textAlign: "center"
};

const tableCardSx = {
  background: "#fff",
  borderRadius: "24px",
  overflow: "hidden",
  boxShadow: "0px 0px 4px rgba(30,39,79,0.12)"
};

const headerRowSx = {
  display: "flex",
  alignItems: "center",
  height: "48px",
  background: semanticColors.gradient.surface,
  px: "20px"
};

const nameColSx = { flex: 1 };
const sexColSx = { width: "80px", flexShrink: 0 };
const ageColSx = { width: "72px", flexShrink: 0 };
const snsColSx = { width: "130px", flexShrink: 0 };

const headerCellSx = { ...typography.body.lBold, color: "#fff" };

const rowSx = (index: number) => ({
  display: "flex",
  alignItems: "center",
  height: "64px",
  px: "20px",
  cursor: "pointer",
  bgcolor: semanticColors.surface.canvas,
  borderTop: index > 0 ? `0.5px solid ${semanticColors.border.light}` : "none",
  "&:hover": { bgcolor: semanticColors.highlight.low },
  transition: "background-color 0.15s ease"
});

const nameCellSx = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const avatarSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  border: `1.5px solid ${semanticColors.border.middle}`
};

const nameWrapSx = {
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: 0.75
};

const nameTextSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary,
  textTransform: "uppercase",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const cellTextSx = {
  ...typography.body.l,
  color: semanticColors.text.primary
};

const snsTextSx = {
  ...typography.body.l,
  color: semanticColors.text.secondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const overlaySx = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9,
  bgcolor: "rgba(30,39,79,0.18)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  overflowY: "auto",
  pt: "144px",
  px: { xs: "16px", sm: "24px" },
  pb: "48px"
};

const detailCardSx = {
  maxWidth: "600px",
  mx: "auto",
  bgcolor: "#fff",
  borderRadius: "24px",
  boxShadow: "0 8px 32px rgba(30,39,79,0.2)",
  p: "24px",
  position: "relative",
  animation: "fadeScaleIn 0.25s ease",
  "@keyframes fadeScaleIn": {
    from: { opacity: 0, transform: "scale(0.96) translateY(10px)" },
    to: { opacity: 1, transform: "scale(1) translateY(0)" }
  }
};

const closeBtnSx = { position: "absolute", top: 12, right: 12 };

const paymentBtnSx = {
  position: "absolute",
  top: 12,
  right: 46,
  bgcolor: "#F5A623",
  color: "#fff",
  width: 32,
  height: 32,
  "&:hover": { bgcolor: "#E6961A" }
};

export default function Utentes() {
  const { t } = useTranslation();
  const { token } = useUserState();
  const history = useHistory();
  const location = useLocation<{ patientName?: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState(location.state?.patientName ?? "");

  // The detail card and the scheduling dialog live in the URL query so the
  // browser back button closes them (?patient=<id> opens the card, adding
  // &video=1 opens the dialog over it).
  const params = new URLSearchParams(location.search);
  const selectedId = params.get("patient");
  const selectedPatient = selectedId
    ? patients.find(p => String(p.nhsId ?? p._id) === selectedId) ?? null
    : null;
  const videoDialogPatient =
    params.get("video") === "1" ? selectedPatient : null;

  const patientPath = (patient: any) => String(patient.nhsId ?? patient._id);

  // Close by stripping the query (via replace) so it works even on a directly
  // opened link, where there'd be no in-app entry to go back to. The browser
  // back button still closes them too, since they're derived from the URL.
  const closeCard = () =>
    history.replace({ pathname: location.pathname, search: "" });
  const closeVideo = () => {
    const p = new URLSearchParams(location.search);
    p.delete("video");
    history.replace({ pathname: location.pathname, search: p.toString() });
  };

  const { upcoming } = useAppointments();
  const patientsWithUpcoming = useMemo(() => {
    const now = Date.now();
    return new Set(
      upcoming
        .filter(a => a.start.getTime() >= now && a.clinicalFileId)
        .map(a => a.clinicalFileId as string)
    );
  }, [upcoming]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const records = await fetchFichasClinicas(token);
        setPatients(Array.isArray(records) ? records : records?.items ?? []);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const matches = patients.filter(
      (u: any) =>
        !q ||
        (u.name ?? "").toLowerCase().includes(q) ||
        (u.nhsId ?? "").includes(q)
    );

    const accessTime = (u: any) => {
      const raw = u.lastAccessed;
      if (!raw) return NaN;
      const t = new Date(raw).getTime();
      return isNaN(t) ? NaN : t;
    };

    // Accessed first (most recent on top); those never opened go to the
    // bottom, sorted alphabetically by name.
    return [...matches].sort((a: any, b: any) => {
      const ta = accessTime(a);
      const tb = accessTime(b);
      const aHas = !isNaN(ta);
      const bHas = !isNaN(tb);
      if (aHas && bHas) return tb - ta;
      if (aHas) return -1;
      if (bHas) return 1;
      return (a.name ?? "").localeCompare(b.name ?? "", "pt", {
        sensitivity: "base"
      });
    });
  }, [patients, search]);

  const handlePatientClick = (patient: any) => {
    if (isMobile) {
      history.push(`/patients/${patient.nhsId ?? patient._id}`);
      return;
    }
    // Clicking the already-open patient closes its card, otherwise open it.
    if (selectedId === patientPath(patient)) {
      closeCard();
    } else {
      history.push({
        pathname: location.pathname,
        search: `?patient=${patientPath(patient)}`
      });
    }
  };

  return (
    <Box sx={pageSx}>
      {/* Sticky tabs — same pattern as other pages */}
      <Box sx={stickyBarSx}>
        <Tabs value={0} onChange={() => {}} sx={tabsSx}>
          <Tab label="Utentes" sx={utentesTabSx} />
        </Tabs>
      </Box>

      {/* Centered list content */}
      <Box sx={contentSx}>
        <TextField
          fullWidth
          placeholder={t("utentes.searchPlaceholder") as string}
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={searchIconSx} />
              </InputAdornment>
            )
          }}
          sx={searchFieldSx}
        />

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={10}
          >
            <Loading />
          </Box>
        ) : error || filtered.length === 0 ? (
          <Typography sx={noResultsSx}>{t("common.noResults")}</Typography>
        ) : (
          <Box sx={tableCardSx}>
            {/* Header row */}
            <Box sx={headerRowSx}>
              <Box sx={nameColSx}>
                <Typography sx={headerCellSx}>Nome</Typography>
              </Box>
              <Box sx={sexColSx}>
                <Typography sx={headerCellSx}>Sexo</Typography>
              </Box>
              <Box sx={ageColSx}>
                <Typography sx={headerCellSx}>Idade</Typography>
              </Box>
              <Box sx={snsColSx}>
                <Typography sx={headerCellSx}>N.º SNS</Typography>
              </Box>
            </Box>

            {/* Data rows */}
            {filtered.map((u: any, i: number) => {
              const gender = u.gender === 1 ? "M" : u.gender === 2 ? "F" : "-";
              const age = calculateAge(u.birthDate);
              const hasVideo = patientsWithUpcoming.has(
                u.clinicalFileId ?? u.fileId
              );

              return (
                <Box
                  key={u._id ?? i}
                  onClick={() => handlePatientClick(u)}
                  sx={rowSx(i)}
                >
                  <Box sx={nameCellSx}>
                    <Avatar src={u.pictureUrl} alt={u.name} sx={avatarSx} />
                    <Box sx={nameWrapSx}>
                      <Typography sx={nameTextSx}>{u.name}</Typography>
                      {hasVideo && (
                        <Tooltip title={t("utentes.hasScheduledVideo")}>
                          <span
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <FontAwesomeIcon
                              icon={faVideo}
                              style={{
                                fontSize: 13,
                                color: "#0DBDA7",
                                flexShrink: 0
                              }}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box sx={sexColSx}>
                    <Typography sx={cellTextSx}>{gender}</Typography>
                  </Box>
                  <Box sx={ageColSx}>
                    <Typography sx={cellTextSx}>{age}</Typography>
                  </Box>
                  <Box sx={snsColSx}>
                    <Typography sx={snsTextSx}>{u.nhsId ?? "-"}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Patient detail — blurred overlay card */}
      {selectedPatient && (
        <Box onClick={closeCard} sx={overlaySx}>
          <Box onClick={e => e.stopPropagation()} sx={detailCardSx}>
            <IconButton size="small" onClick={closeCard} sx={closeBtnSx}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <Tooltip title={t("patientDetail.requestPayment")}>
              <IconButton
                size="small"
                sx={paymentBtnSx}
                onClick={() =>
                  history.push({
                    pathname: `/patients/${patientPath(
                      selectedPatient
                    )}/payments/new`,
                    // Remember the card that was open so the payment screen can
                    // come back to it rather than guessing a destination.
                    state: { from: `${location.pathname}${location.search}` }
                  })
                }
              >
                <EuroIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <PatientDetailPanel
              patient={selectedPatient}
              onVideoCall={() =>
                history.push({
                  pathname: location.pathname,
                  search: `?patient=${patientPath(selectedPatient)}&video=1`
                })
              }
              onClinicalRecord={() =>
                history.push(
                  `/patients/${patientPath(selectedPatient)}/clinical-records`,
                  { patient: selectedPatient }
                )
              }
            />
          </Box>
        </Box>
      )}

      {videoDialogPatient && (
        <PatientVideoDialog
          patientName={videoDialogPatient.name ?? ""}
          patient={videoDialogPatient}
          open={!!videoDialogPatient}
          onClose={closeVideo}
        />
      )}
    </Box>
  );
}
